export const getLoggedInUserEmail = () => {
  try {
    const raw = localStorage.getItem('employmeUser')
    if (!raw) return ''
    const user = JSON.parse(raw)
    return user?.email || ''
  } catch {
    return ''
  }
}

export const hasUploadedCv = (cvUrl) => Boolean(cvUrl && cvUrl !== 'MANUAL_ENTRY')

export const parseProfileSkills = (profile) => {
  if (Array.isArray(profile?.skills)) return profile.skills.filter(Boolean)
  if (typeof profile?.skills === 'string' && profile.skills) {
    return profile.skills.split(',').map((s) => s.trim()).filter(Boolean)
  }
  return []
}

export const profileToFormState = (profile) => {
  const userEmail = getLoggedInUserEmail()
  return {
    cvUrl: profile.cvUrl || '',
    cvViewUrl: profile.cvViewUrl || '',
    fullName: profile.fullName || '',
    contactEmail: userEmail || profile.contactEmail || '',
    contactPhone: profile.contactPhone || '',
    locationCountry: profile.location?.country || '',
    locationLat:
      typeof profile.location?.lat === 'number' && !Number.isNaN(profile.location.lat)
        ? String(profile.location.lat)
        : '',
    locationLng:
      typeof profile.location?.lng === 'number' && !Number.isNaN(profile.location.lng)
        ? String(profile.location.lng)
        : '',
    specialization: profile.specialization || '',
    skills: parseProfileSkills(profile),
    educationSummary: profile.educationSummary || '',
    yearsOfExperience:
      typeof profile.yearsOfExperience === 'number' &&
      !Number.isNaN(profile.yearsOfExperience) &&
      profile.yearsOfExperience > 0
        ? String(profile.yearsOfExperience)
        : '',
    experienceSummary: profile.experienceSummary || ''
  }
}

export const formToProfilePayload = (form) => {
  const payload = {
    cvUrl: form.cvUrl || 'MANUAL_ENTRY',
    fullName: form.fullName || undefined,
    contactEmail: form.contactEmail || getLoggedInUserEmail() || undefined,
    contactPhone: form.contactPhone || undefined,
    location: {
      country: form.locationCountry || '',
      lat: form.locationLat ? Number(form.locationLat) : undefined,
      lng: form.locationLng ? Number(form.locationLng) : undefined
    },
    specialization: form.specialization || undefined,
    skills: form.skills,
    educationSummary: form.educationSummary || undefined,
    experienceSummary: form.experienceSummary || undefined
  }

  if (form.yearsOfExperience !== '') {
    const parsedYears = Number(form.yearsOfExperience)
    if (Number.isFinite(parsedYears)) {
      payload.yearsOfExperience = parsedYears
    }
  }

  return payload
}

export const inputClass =
  'w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-800 shadow-sm transition-all duration-200 placeholder:text-slate-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20'

export const labelClass = 'block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5'

const hasProfileLocation = (profile) => {
  const loc = profile?.location
  if (!loc) return false
  if (loc.country?.trim()) return true
  return Number.isFinite(loc.lat) && Number.isFinite(loc.lng)
}

const hasProfileExperience = (profile) => {
  if (profile?.yearsOfExperience > 0) return true
  if (profile?.experienceSummary?.trim()) return true
  return Array.isArray(profile?.workHistory) && profile.workHistory.length > 0
}

const hasProfileEducation = (profile) => {
  if (profile?.educationSummary?.trim()) return true
  return Array.isArray(profile?.education) && profile.education.length > 0
}

export const computeProfileStrength = (profile) => {
  if (!profile) return 0

  let score = 0
  if (profile.fullName?.trim()) score += 15
  if (profile.contactEmail?.trim() || profile.contactPhone?.trim()) score += 15
  if (hasUploadedCv(profile.cvUrl)) score += 10
  if (hasProfileLocation(profile)) score += 10
  if (profile.specialization?.trim()) score += 10
  if (parseProfileSkills(profile).length > 0) score += 15
  if (hasProfileExperience(profile)) score += 15
  if (hasProfileEducation(profile)) score += 10

  return Math.min(100, score)
}

export const getProfileStrengthInfo = (profile) => {
  const percentage = computeProfileStrength(profile)

  let label = 'Incomplete'
  let helper = 'Add more details like your CV, skills, and experience to stand out.'

  if (percentage >= 80) {
    label = 'Ready to apply'
    helper = 'Your profile looks strong. Focus on tailored answers in your video interviews.'
  } else if (percentage >= 50) {
    label = 'Getting there'
    helper = 'Complete your profile with more experience, education, or skills to improve your matches.'
  }

  return { percentage, label, helper }
}
